import { handleResponse } from '../_helpers';
import { API_KEY, config } from "../../common/constant";
import { decryptData } from '../../common';

export const OpenAIService = {
    sendMessage,
    startAssistantRun,
    pollRunStatus,
    getMessages,
    streamAssistantResponse,
    cancelRun,
    getActiveRun,
    getSpeechAudioFromOpenAI,
    transcribeAudio,
    AIChatbot,
    AIChatbotFileUpload,
    fileUpload
};

const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${decryptData(API_KEY)}`,
    "OpenAI-Beta": "assistants=v2"
};

async function AIChatbot(payload) {
    const requestOptions = {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(payload),
    };
    const url = config.BASE_URL_AICHATBOT + `/api/AIChatbot/ask`;
    return fetch(url, requestOptions)
        .then(handleResponse)
        .then(result => {
                return result;
        });
}

async function AIChatbotFileUpload(formData) {
    const url = "https://dev-chatbot-api.givepls.com/api/AIChatbot/upload";
    return fetch(url, {
        method: 'POST',
        body: formData, // Don't set Content-Type header, let the browser set it
    })
    .then(handleResponse)
    .then(result => {
        return result;
    });
}

/**
 * Sends a message to the OpenAI API.
 * @param {string} sessionId
 * @param {string} messageText
 * @param {string|null} fileId
 */
async function sendMessage(sessionId, messageText, fileId = null) {
    const promptText = fileId
    ? `${messageText} Please analyze the uploaded file I've attached.`
    : messageText;

    const requestData = {
        role: "user",
        content: promptText,
        ...(fileId && { attachments: [{ file_id: fileId, tools: [{ type: "file_search" }] }] })
    };

    const requestOptions = {
        method: "POST",
        headers,
        body: JSON.stringify(requestData),
    };

    const url = `${config.OPENAI_BASE_URL}/threads/${sessionId}/messages`;

    try {
        const response = await fetch(url, requestOptions);
        const data = await response.json();
        if (!response.ok) {
            return {
                error: data?.error?.message || "Something went wrong",
                isError: true,
                status: response.status,
            };
        }
        return data;
    } catch (err) {
        console.error("Fetch error:", err);
        return { error: err?.message || "Something went wrong", isError: true };
    }
}

/**
 * Starts an assistant run.
 * @param {string} sessionId
 * @param {string} assistantId
 * @param {string|null} fileId
 */
async function startAssistantRun(sessionId, assistantId, fileId = null, onMessage, onStreamComplete) {
    const requestOptions = {
        method: "POST",
        headers,
        body: JSON.stringify({
            assistant_id: assistantId,
            stream: true,
            tools: fileId ? [{ type: "file_search" }] : []
        }),
    };

    const url = `${config.OPENAI_BASE_URL}/threads/${sessionId}/runs`;

    const response = await fetch(url, requestOptions);
    if (!response.body) {
        throw new Error("No response body from API");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let botResponse = "";

    while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });

        // Process each line
        const lines = chunk.split("\n").filter(line => line.trim() !== "");
        lines.forEach((line) => {
            if (line.startsWith("data: [DONE]")) {
                if (onStreamComplete) onStreamComplete(true);
                if (onMessage) onMessage(botResponse);
                return botResponse; // Return final response
            }

            if (line.startsWith("data:")) {
                try {
                    const json = JSON.parse(line.replace("data: ", ""));
                    const text = json?.delta?.content?.[0]?.text?.value || "";

                    if (text) {
                        botResponse += text;
                        if (onMessage) onMessage(botResponse); // Real-time UI update
                    }

                } catch (error) {
                    console.error("Error parsing stream chunk:", error);
                }
            }
        });
    }
    return botResponse; // Return final response after stream ends
}

/**
 * Polls the assistant run status until it's completed or timeout occurs.
 * @param {string} sessionId
 * @param {string} runId
 * @param {number} maxRetries
 * @param {number} delay
 */
async function pollRunStatus(sessionId, runId, maxRetries = 15, delay = 2000) {
    let retries = 0;
    let status = "in_progress";

    while (["queued", "in_progress", "running"].includes(status)) {
        if (retries >= maxRetries) {
            throw new Error("Polling timeout: Assistant took too long to respond.");
        }

        await new Promise(resolve => setTimeout(resolve, delay));
        retries++;

        const url = `${config.OPENAI_BASE_URL}/threads/${sessionId}/runs/${runId}`;
        const requestOptions = { headers };

        const response = await fetch(url, requestOptions);
        if (!response.ok) throw new Error("Failed to fetch run status.");

        const data = await response.json();
        status = data.status;

        console.log(`Polling attempt ${retries}: Status - ${status}`);

        if (status === "completed") return true;
    }

    return false;
}

/**
 * Retrieves messages from OpenAI API.
 * @param {string} sessionId
 */
async function getMessages(sessionId) {
    const requestOptions = { headers };

    const url = `${config.OPENAI_BASE_URL}/threads/${sessionId}/messages`;

    return fetch(url, requestOptions).then(handleResponse);
}


async function streamAssistantResponse(sessionId, runId, onChunk) {
    const response = await fetch(`https://api.openai.com/v1/assistants/${runId}/messages/stream`, {
        headers
    });

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunkText = decoder.decode(value, { stream: true });
        onChunk(chunkText);
    }
};

async function getActiveRun(threadId) {
    try {
        const response = await fetch(`https://api.openai.com/v1/threads/${threadId}/runs`, {
            method: 'GET',
            headers
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error?.message || 'Failed to fetch runs');

        // Find an active run (status: "in_progress" or "queued")
        return data.data.find(run => run.status === "in_progress" || run.status === "queued") || null;
    } catch (error) {
        console.error("Error fetching active run:", error);
        return null;
    }
};

async function cancelRun(threadId, runId) {
    try {
        const response = await fetch(`https://api.openai.com/v1/threads/${threadId}/runs/${runId}/cancel`, {
            method: 'POST',
            headers
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error?.message || 'Failed to cancel run');

        console.log("Run cancelled successfully:", data);
    } catch (error) {
        console.error("Error cancelling run:", error);
    }
};

async function getSpeechAudioFromOpenAI(text) {
    const response = await fetch("https://api.openai.com/v1/audio/speech", {
      method: "POST",
      headers,
      body: JSON.stringify({
        model: "tts-1",
        input: text,
        voice: "shimmer",
        response_format: "wav",
      })
    });

    if (!response.ok) throw new Error('TTS API request failed');

  const reader = response.body.getReader();
  const chunks = [];
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  const blob = new Blob(chunks, { type: 'audio/wav' });
  return URL.createObjectURL(blob);
};

async function transcribeAudio(formData) {
    try {
      const response = await fetch('https://api.openai.com/v1/audio/transcriptions', {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${decryptData(API_KEY)}` // ✅ Set only Authorization here
          },
        body: formData
      });
  
      const data = await response.json();
      return data.text;
    } catch (err) {
      console.error('Transcription failed:', err);
    }
};

async function fileUpload(formData) {
    try {
        const uploadResponse = await fetch('https://api.openai.com/v1/files', {
            method: 'POST',
            headers: {
               Authorization: `Bearer ${decryptData(API_KEY)}`,
                'OpenAI-Beta': 'assistants=v2'
            },
            body: formData
        });

        if (!uploadResponse.ok) {
            const errorData = await uploadResponse.json();
            throw new Error(errorData.error?.message || 'File upload failed.');
        }

        const uploadData = await uploadResponse.json();
        return uploadData;
    } catch (err) {
      console.error('Transcription failed:', err);
    }
};
  
  

